import React from 'react';
import { Button } from '@components/common/button';
import { LoadingSpinner } from '@components/common/loading_spinner';
import {
  ArrowLeft,
  Download,
  Send,
  Printer,
  Edit,
  Calendar,
  User,
  FileText,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Invoice, InvoiceTemplate as InvoiceTemplateType } from '@/types/invoice_types';

interface InvoicePreviewProps {
  invoice: Invoice | null;
  template_config: InvoiceTemplateType;
  on_download?: (invoice: Invoice) => void;
  on_send?: (invoice: Invoice) => void;
  on_edit?: (invoice: Invoice) => void;
  on_print?: () => void;
  on_back?: () => void;
  is_loading?: boolean;
}

export const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoice,
  template_config,
  on_download,
  on_send,
  on_edit,
  on_print,
  on_back,
  is_loading = false
}) => {
  if (is_loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading invoice..." />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <FileText size={48} className="mx-auto text-medium-gray mb-4" />
          <p className="text-medium-gray">Invoice not found</p>
          <Button variant="primary" onClick={on_back} className="mt-4">
            Back to Invoices
          </Button>
        </div>
      </div>
    );
  }

  const format_currency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `KES ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const format_date = (date_string: string) => {
    return new Date(date_string).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const get_status_config = (status: string) => {
    const status_lower = status.toLowerCase();
    const configs = {
      draft: { 
        bg: 'bg-medium-gray bg-opacity-10', 
        text: 'text-medium-gray', 
        border: 'border-medium-gray',
        icon: <Edit size={16} />
      },
      sent: { 
        bg: 'bg-info bg-opacity-10', 
        text: 'text-info', 
        border: 'border-info',
        icon: <Send size={16} />
      },
      paid: { 
        bg: 'bg-success bg-opacity-10', 
        text: 'text-success', 
        border: 'border-success',
        icon: <CheckCircle size={16} />
      },
      overdue: { 
        bg: 'bg-error bg-opacity-10', 
        text: 'text-error', 
        border: 'border-error',
        icon: <AlertCircle size={16} />
      },
      cancelled: { 
        bg: 'bg-light-gray', 
        text: 'text-medium-gray', 
        border: 'border-medium-gray',
        icon: <AlertCircle size={16} />
      }
    };
    return configs[status_lower as keyof typeof configs] || configs.draft;
  };

  const get_status_badge = (status: string) => {
    const config = get_status_config(status);
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold border ${config.bg} ${config.text} ${config.border}`}>
        {config.icon}
        {status.toUpperCase()}
      </span>
    );
  };

  const subtotal = invoice.items?.reduce((sum, item) => sum + parseFloat((item as any).amount), 0) || 0;
  const tax_amount = parseFloat((invoice as any).total_amount) - subtotal;
  const amount_outstanding = parseFloat((invoice as any).amount_outstanding || '0');
  const amount_paid = parseFloat((invoice as any).amount_paid || '0');

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white/95 border-b border-light-gray sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={on_back}
                icon_left={<ArrowLeft size={18} />}
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-charcoal">Invoice Preview</h1>
                <p className="text-sm text-medium-gray mt-0.5">
                  {invoice.invoice_number}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {on_edit && invoice.status.toLowerCase() === 'draft' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => on_edit(invoice)}
                  icon_left={<Edit size={18} />}
                >
                  Edit
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={on_print}
                icon_left={<Printer size={18} />}
              >
                Print
              </Button>
              {on_download && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => on_download(invoice)}
                  icon_left={<Download size={18} />}
                >
                  Download PDF
                </Button>
              )}
              {on_send && invoice.status.toLowerCase() === 'draft' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => on_send(invoice)}
                  icon_left={<Send size={18} />}
                >
                  Send to Client
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Status Banner */}
        <div className="mb-6">
          {invoice.status.toLowerCase() === 'draft' && (
            <div className="p-4 bg-info bg-opacity-5 border border-info rounded-md flex items-start gap-3">
              <Edit size={20} className="text-info flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-charcoal mb-1">Draft Invoice</p>
                <p className="text-xs text-medium-gray">
                  This invoice is in draft status. You can send it to the client via email or download it as PDF for manual delivery.
                </p>
              </div>
            </div>
          )}

          {invoice.status.toLowerCase() === 'sent' && amount_outstanding > 0 && (
            <div className="p-4 bg-warning bg-opacity-5 border border-warning rounded-md flex items-start gap-3">
              <Clock size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-charcoal mb-1">Pending Payment</p>
                <p className="text-xs text-medium-gray">
                  This invoice has been sent to the client. Payment of {format_currency(amount_outstanding)} is pending.
                </p>
              </div>
            </div>
          )}

          {invoice.status.toLowerCase() === 'paid' && (
            <div className="p-4 bg-success bg-opacity-5 border border-success rounded-md flex items-start gap-3">
              <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-charcoal mb-1">Paid Invoice</p>
                <p className="text-xs text-medium-gray">
                  This invoice has been fully paid. Thank you for your payment!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-5 rounded-md border border-light-gray">
            <div className="flex items-center gap-2 text-medium-gray mb-2">
              <FileText size={18} />
              <p className="text-sm font-medium">Status</p>
            </div>
            <div>{get_status_badge(invoice.status)}</div>
          </div>

          <div className="bg-white p-5 rounded-md border border-light-gray">
            <div className="flex items-center gap-2 text-medium-gray mb-2">
              <DollarSign size={18} />
              <p className="text-sm font-medium">Total Amount</p>
            </div>
            <p className="text-2xl font-bold text-forest-green">
              {format_currency(invoice.total_amount)}
            </p>
          </div>

          <div className="bg-white p-5 rounded-md border border-light-gray">
            <div className="flex items-center gap-2 text-medium-gray mb-2">
              <AlertCircle size={18} />
              <p className="text-sm font-medium">Balance Due</p>
            </div>
            <p className={`text-2xl font-bold ${
              amount_outstanding > 0 ? 'text-error' : 'text-success'
            }`}>
              {format_currency(amount_outstanding)}
            </p>
          </div>
        </div>

        {/* Invoice Document */}
        <div className="bg-white rounded-md border border-light-gray overflow-hidden">
          <div className="p-8 md:p-12 space-y-8">
            {/* Invoice Header */}
            <div className="border-b border-light-gray pb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-charcoal mb-2">
                    {(template_config as any).company_name || 'Your Company'}
                  </h2>
                  <p className="text-sm text-medium-gray whitespace-pre-line">
                    {(template_config as any).company_address}
                  </p>
                  {(template_config as any).company_phone && (
                    <p className="text-sm text-medium-gray mt-1">
                      Phone: {(template_config as any).company_phone}
                    </p>
                  )}
                  {(template_config as any).company_email && (
                    <p className="text-sm text-medium-gray">
                      Email: {(template_config as any).company_email}
                    </p>
                  )}
                </div>
                
                <div className="text-right">
                  <h3 className="text-2xl font-bold text-forest-green mb-2">INVOICE</h3>
                  <p className="text-lg font-semibold text-charcoal">
                    {invoice.invoice_number}
                  </p>
                </div>
              </div>
            </div>

            {/* Bill To & Invoice Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bill To */}
              <div>
                <h4 className="text-sm font-semibold text-medium-gray uppercase tracking-wide mb-3">
                  Bill To
                </h4>
                <div className="bg-off-white rounded-md p-5 border border-light-gray">
                  <p className="font-bold text-charcoal text-lg mb-2">
                    {invoice.client_name}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-medium-gray">
                    <User size={14} />
                    <span className="capitalize">{invoice.client_type?.toLowerCase()}</span>
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div>
                <h4 className="text-sm font-semibold text-medium-gray uppercase tracking-wide mb-3">
                  Invoice Details
                </h4>
                <div className="bg-off-white rounded-md p-5 border border-light-gray space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-medium-gray flex items-center gap-2">
                      <Calendar size={14} />
                      Issue Date:
                    </span>
                    <span className="font-semibold text-charcoal">
                      {format_date((invoice as any).issue_date)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-medium-gray flex items-center gap-2">
                      <Calendar size={14} />
                      Due Date:
                    </span>
                    <span className="font-semibold text-charcoal">
                      {format_date(invoice.due_date)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-light-gray">
                    <span className="text-medium-gray">Billing Period:</span>
                    <span className="font-semibold text-charcoal text-right text-xs">
                      {format_date(invoice.billing_period_start)} - {format_date(invoice.billing_period_end)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <h4 className="text-sm font-semibold text-medium-gray uppercase tracking-wide mb-3 flex items-center gap-2">
                <FileText size={14} />
                Line Items
              </h4>
              
              <div className="border border-light-gray rounded-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-off-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-medium-gray uppercase tracking-wide">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-medium-gray uppercase tracking-wide">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-medium-gray uppercase tracking-wide">
                        Unit Price
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-medium-gray uppercase tracking-wide">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-light-gray bg-white">
                    {invoice.items && invoice.items.length > 0 ? (
                      invoice.items.map((item, index) => (
                        <tr key={item.id || index} className="hover:bg-off-white transition-colors">
                          <td className="px-4 py-4">
                            <div className="font-medium text-charcoal">{item.description}</div>
                            {(item as any).notes && (
                              <div className="text-xs text-medium-gray mt-1">{(item as any).notes}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right text-charcoal">
                            {parseFloat((item as any).quantity).toFixed(2)}
                          </td>
                          <td className="px-4 py-4 text-right text-charcoal">
                            {format_currency(item.unit_price)}
                          </td>
                          <td className="px-4 py-4 text-right font-semibold text-charcoal">
                            {format_currency((item as any).amount)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-medium-gray">
                          No items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-full max-w-md">
                <div className="bg-off-white rounded-md p-6 border border-light-gray space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-medium-gray">Subtotal:</span>
                    <span className="font-semibold text-charcoal">{format_currency(subtotal)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-medium-gray">Tax ({(template_config as any).tax_label || 'VAT'}):</span>
                    <span className="font-semibold text-charcoal">{format_currency(tax_amount)}</span>
                  </div>

                  <div className="pt-3 border-t-2 border-forest-green">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-charcoal flex items-center gap-2">
                        <DollarSign size={20} className="text-forest-green" />
                        Total:
                      </span>
                      <span className="text-2xl font-bold text-forest-green">
                        {format_currency(invoice.total_amount)}
                      </span>
                    </div>
                  </div>

                  {amount_paid > 0 && (
                    <>
                      <div className="flex items-center justify-between text-sm pt-3 border-t border-light-gray">
                        <span className="text-medium-gray">Amount Paid:</span>
                        <span className="font-semibold text-success">
                          {format_currency(amount_paid)}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-charcoal">Amount Due:</span>
                        <span className="text-xl font-bold text-error">
                          {format_currency(amount_outstanding)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Notes & Terms */}
            <div className="space-y-4">
              {invoice.notes && (
                <div className="bg-info bg-opacity-5 border border-info rounded-md p-4">
                  <h5 className="text-sm font-semibold text-charcoal mb-2">Notes</h5>
                  <p className="text-sm text-charcoal whitespace-pre-line">{invoice.notes}</p>
                </div>
              )}

              {(template_config as any).default_payment_terms && (
                <div className="bg-off-white rounded-md p-4 border border-light-gray">
                  <h5 className="text-sm font-semibold text-charcoal mb-2">Payment Terms</h5>
                  <p className="text-sm text-medium-gray whitespace-pre-line">
                    {(template_config as any).default_payment_terms}
                  </p>
                </div>
              )}

              {((template_config as any).bank_name || (template_config as any).account_number) && (
                <div className="bg-success bg-opacity-5 border border-success rounded-md p-4">
                  <h5 className="text-sm font-semibold text-charcoal mb-3">Payment Information</h5>
                  <div className="space-y-2 text-sm">
                    {(template_config as any).bank_name && (
                      <div className="flex justify-between">
                        <span className="text-medium-gray">Bank:</span>
                        <span className="font-semibold text-charcoal">{(template_config as any).bank_name}</span>
                      </div>
                    )}
                    {(template_config as any).account_name && (
                      <div className="flex justify-between">
                        <span className="text-medium-gray">Account Name:</span>
                        <span className="font-semibold text-charcoal">{(template_config as any).account_name}</span>
                      </div>
                    )}
                    {(template_config as any).account_number && (
                      <div className="flex justify-between">
                        <span className="text-medium-gray">Account Number:</span>
                        <span className="font-semibold text-charcoal font-mono">{(template_config as any).account_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {(template_config as any).footer_text && (
              <div className="pt-6 border-t border-light-gray text-center">
                <p className="text-xs text-medium-gray">{(template_config as any).footer_text}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};